import React, { useMemo, useState } from "react";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import { toast } from "react-toastify";
import {
  Button,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "reactstrap";

import {
  adjustProductStock,
  markProductBatchOpened,
} from "../../../../services/productService";
import { SuggestMarkOpenedItem } from "../../../../types/stockMovement";

interface ProductStockAdjustModalProps {
  isOpen: boolean;
  productId: number | null;
  onClose: () => void;
  onSuccess: () => void;
}

interface AdjustFormValues {
  quantity: string;
  reason: string;
  expires_at: string;
}

const emptyValues = (): AdjustFormValues => ({
  quantity: "",
  reason: "",
  expires_at: "",
});

const ProductStockAdjustModal = ({
  isOpen,
  productId,
  onClose,
  onSuccess,
}: ProductStockAdjustModalProps) => {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [suggestItems, setSuggestItems] = useState<SuggestMarkOpenedItem[]>(
    []
  );
  const [markingOpened, setMarkingOpened] = useState(false);

  const validationSchema = useMemo(
    () =>
      Yup.object({
        quantity: Yup.number()
          .typeError(t("studio.products.stockHistory.adjust.validation.quantityType"))
          .required(t("studio.products.stockHistory.adjust.validation.quantityRequired"))
          .notOneOf([0], t("studio.products.stockHistory.adjust.validation.quantityNotZero"))
          .integer(t("studio.products.stockHistory.adjust.validation.quantityType")),
        reason: Yup.string()
          .trim()
          .required(t("studio.products.stockHistory.adjust.validation.reasonRequired"))
          .min(3, t("studio.products.stockHistory.adjust.validation.reasonMin"))
          .max(2000, t("studio.products.stockHistory.adjust.validation.reasonMax")),
      }),
    [t]
  );

  const formik = useFormik<AdjustFormValues>({
    enableReinitialize: true,
    initialValues: emptyValues(),
    validationSchema,
    onSubmit: async (values) => {
      if (productId === null) {
        return;
      }

      setSubmitting(true);

      try {
        const quantity = Number(values.quantity);
        const response = await adjustProductStock(productId, {
          quantity,
          reason: values.reason.trim(),
          ...(quantity > 0 && values.expires_at
            ? { expires_at: values.expires_at }
            : {}),
        });
        toast.success(response.message);
        formik.resetForm();
        onSuccess();

        const suggestions = response.data?.suggest_mark_opened ?? [];
        if (quantity < 0 && suggestions.length > 0) {
          setSuggestItems(suggestions);
        } else {
          onClose();
        }
      } catch (error: any) {
        toast.error(error?.data || t("studio.products.stockHistory.adjust.toast.error"));
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleClose = () => {
    formik.resetForm();
    setSuggestItems([]);
    onClose();
  };

  const handleMarkOpenedWithProductDefault = async () => {
    setMarkingOpened(true);
    try {
      for (const item of suggestItems) {
        await markProductBatchOpened(item.product_id, item.batch_id);
      }
      toast.success(t("studio.products.batches.suggestMarkOpened.success"));
      onSuccess();
      handleClose();
    } catch (error: any) {
      toast.error(
        error?.data || t("studio.products.batches.suggestMarkOpened.error")
      );
    } finally {
      setMarkingOpened(false);
    }
  };

  const productNames = suggestItems
    .map((item) => item.product_name)
    .filter(Boolean)
    .join(", ");

  return (
    <>
      <Modal isOpen={isOpen && suggestItems.length === 0} toggle={handleClose} centered>
        <ModalHeader toggle={handleClose}>
          {t("studio.products.stockHistory.adjust.title")}
        </ModalHeader>
        <Form onSubmit={formik.handleSubmit}>
          <ModalBody>
            <p className="text-muted">{t("studio.products.stockHistory.adjust.description")}</p>
            <div className="mb-3">
              <Label htmlFor="stock-adjust-quantity">
                {t("studio.products.stockHistory.adjust.fields.quantity.label")}
              </Label>
              <Input
                id="stock-adjust-quantity"
                name="quantity"
                type="number"
                step={1}
                value={formik.values.quantity}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder={t("studio.products.stockHistory.adjust.fields.quantity.placeholder")}
                invalid={!!(formik.touched.quantity && formik.errors.quantity)}
              />
              <div className="form-text">
                {t("studio.products.stockHistory.adjust.fields.quantity.hint")}
              </div>
              <FormFeedback>{formik.errors.quantity}</FormFeedback>
            </div>
            <div className="mb-3">
              <Label htmlFor="stock-adjust-reason">
                {t("studio.products.stockHistory.adjust.fields.reason.label")}
              </Label>
              <Input
                id="stock-adjust-reason"
                name="reason"
                type="textarea"
                rows={4}
                value={formik.values.reason}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder={t("studio.products.stockHistory.adjust.fields.reason.placeholder")}
                invalid={!!(formik.touched.reason && formik.errors.reason)}
              />
              <FormFeedback>{formik.errors.reason}</FormFeedback>
            </div>
            {Number(formik.values.quantity) > 0 ? (
              <div className="mb-0">
                <Label htmlFor="stock-adjust-expires">
                  {t("studio.products.stockHistory.adjust.fields.expiresAt.label")}
                </Label>
                <Input
                  id="stock-adjust-expires"
                  name="expires_at"
                  type="date"
                  value={formik.values.expires_at}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                <div className="form-text">
                  {t("studio.products.stockHistory.adjust.fields.expiresAt.hint")}
                </div>
              </div>
            ) : null}
          </ModalBody>
          <ModalFooter>
            <Button color="light" type="button" onClick={handleClose}>
              {t("studio.products.actions.cancel")}
            </Button>
            <Button color="primary" type="submit" disabled={submitting}>
              {submitting
                ? t("studio.products.loading")
                : t("studio.products.stockHistory.adjust.actions.submit")}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      <Modal
        isOpen={isOpen && suggestItems.length > 0}
        toggle={handleClose}
        centered
      >
        <ModalHeader toggle={handleClose}>
          {t("studio.products.batches.suggestMarkOpened.title")}
        </ModalHeader>
        <ModalBody>
          <p className="mb-2">
            {t("studio.products.batches.suggestMarkOpened.message", {
              products: productNames || "—",
            })}
          </p>
          <p className="text-muted small mb-0">
            {t("studio.products.batches.suggestMarkOpened.defaultHint")}
          </p>
        </ModalBody>
        <ModalFooter>
          <Button color="light" type="button" onClick={handleClose} disabled={markingOpened}>
            {t("studio.products.batches.suggestMarkOpened.cancel")}
          </Button>
          <Button
            color="primary"
            type="button"
            disabled={markingOpened}
            onClick={() => void handleMarkOpenedWithProductDefault()}
          >
            {markingOpened ? (
              <Spinner size="sm" className="me-1" />
            ) : null}
            {t("studio.products.batches.suggestMarkOpened.confirm")}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default ProductStockAdjustModal;
